<style>
.v-timeline-item__opposite {
    align-self: flex-start;
}
</style>

<style scoped>
.quick-report-row .v-timeline-item__opposite {
    align-self: flex-start;
}
.period-title {
    font-weight: bold;
    text-align: center;
    border: 1px solid gray;
    border-radius: 5px;
    z-index: 0;
    background-color: white;
}
</style>

<template>
    <div v-if="loading && !match" class="d-flex align-center justify-center">
        <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
    </div>

    <div v-else>
        <div v-if="twoTeams">
            <teamsport-match-result :match="match" flat :show-live-result="true" :show-location="true" class="mb-8">
                <template #append-center-content v-if="canReportResult || referees.length">
                    <div class="d-flex flex-column justify-center mt-4">
                        <div v-if="canReportResult" class="mb-4">
                            <a href="" @click.prevent="showSetResultDialog = true">{{ $translate('Set result') }}</a>
                        </div>

                        <div class="font-weight-bold">{{$translate('Referees')}}</div>
                        <div v-for="referee in referees" :key="referee.id">
                            <span :class="{'mr-2': referee.mail || referee.phone }">
                                {{ referee.name }}
                                <template v-if="referee.mail || referee.phone && showUserDetails && !$vuetify.breakpoint.mobile">
                                    <span>:</span>
                                </template>
                            </span>
                            <span>
                                <a :href="'mailto:' + referee.mail" v-if="referee.mail && showUserDetails">
                                    <template v-if="$vuetify.breakpoint.mobile">
                                        <v-icon color="primary">mdi-email-outline</v-icon>
                                    </template>
                                    <template v-else>
                                        {{ referee.mail }}
                                    </template>
                                </a>
                                <template v-if="referee.mail && referee.phone && showUserDetails && !$vuetify.breakpoint.mobile">
                                    <span>,</span>
                                </template>
                            </span>
                            <a :href="'tel:' + referee.phone" v-if="referee.phone && showUserDetails" :class="{ 'ml-2': referee.mail }">
                                <template v-if="$vuetify.breakpoint.mobile">
                                    <v-icon color="primary">mdi-phone-outline</v-icon>
                                </template>
                                <template v-else>
                                    {{ referee.phone }}
                                </template>
                            </a>
                        </div>
                    </div>
                </template>
            </teamsport-match-result>

            <template v-if="teamsSet">
                <hr />

                <v-timeline>
                    <v-timeline-item class="text-right quick-report-row" color="primary" fill-dot icon="mdi-plus" large v-if="canLiveReport">
                        <template v-if="canLiveReportForAwayTeam && !$vuetify.breakpoint.mobile">
                            <div class="font-weight-bold">{{$translate('Quick report for {0}').format(match.teams[1].name)}}</div>
                            <a href="#" v-for="eventType in eventTypesForSport" :key="eventType.id" @click.prevent="addEvent(match.teams[1].id, eventType.id)" class="mr-2">{{eventType.name}}</a>
                        </template>
                        <template #opposite v-if="canLiveReportForHomeTeam && !$vuetify.breakpoint.mobile">
                            <div class="text-left">
                                <div class="font-weight-bold">{{$translate('Quick report for {0}').format(match.teams[0].name)}}</div>
                                <a href="#" v-for="eventType in eventTypesForSport" :key="eventType.id" @click.prevent="addEvent(match.teams[0].id, eventType.id)" class="mr-2">{{eventType.name}}</a>
                            </div>
                        </template>
                        <template #icon>
                            <v-icon color="primarytext" large @click="addEvent()">mdi-plus</v-icon>
                        </template>
                    </v-timeline-item>

                    <div class="d-flex justify-center mb-4" v-if="events && events.length == 0">
                        <span class="period-title text--disabled pa-1">
                            {{ $translate('No events') }}
                        </span>
                    </div>
                </v-timeline>
            </template>

            <template v-if="events && events.length > 0">
                <div v-for="timePeriod in timePeriods" :key="timePeriod.id">
                    <div class="d-flex justify-center" v-if="timePeriod.id && groupedEvents[timePeriod.id].length">
                        <span class="period-title text--disabled pa-1">{{timePeriod.title}}</span>
                    </div>
                    <v-timeline v-if="groupedEvents[timePeriod.id].length">
                        <v-timeline-item v-for="event in groupedEvents[timePeriod.id]" :key="event.id" fill-dot :left="event.home" :right="!event.home" :class="{'text-right': event.home}" :color="event.background_color">
                            <span class="font-weight-bold" :style="{ color: event.color }">{{event.title}}</span>
                            <span v-if="event.user">{{event.user.name}}</span>
                            <span v-if="event.comment" class="font-italic">{{event.comment}}</span>
                            <template #icon>
                                <v-btn icon color="primarytext" @click="changeEvent(event)">{{event.time}}</v-btn>
                            </template>
                        </v-timeline-item>
                    </v-timeline>
                </div>
            </template>

            <!-- Team overview -->
            <template v-if="teamsSet && !isIndividualCompetition">
                <hr />
                <v-row>
                    <v-col cols="12" md="6" v-for="team in opposingTeams" :key="team.id"> <!-- index 0: Home team, index 1: Away team -->
                        <TeamOverview :match="match" :members="members" :attendance="attendance[team.id] || []" :team="team" @updateAttendance="updateAttendance"/>
                    </v-col>
                </v-row>
            </template>

            <!-- Dialogs -->
            <TeamsportEventDialog v-model="addEventDialog.show" :match="match" :eventTypeId="addEventDialog.eventTypeId" :teamId="addEventDialog.teamId" v-if="addEventDialog.show" />
            <TeamsportEventDialog v-model="changeEventDialog.show" :match="match" :eventId="changeEventDialog.eventId" v-if="changeEventDialog.show" />
            <TeamsportSetResultDialog v-model="showSetResultDialog" :match="match" v-if="showSetResultDialog" />
        </div>

        <TeamsportOneMatch v-else :matchId="match.id" />
    </div>
</template>

<script>
import TeamsportMatchResult from '../TeamsportMatchResult.vue';
import TeamsportEventDialog from '../TeamsportEventDialog.vue';
import TeamsportSetResultDialog from '../TeamsportSetResultDialog.vue'
import TeamsportOneMatch from '../TeamsportOneMatch.vue';
import TeamOverview from '../../components/Teamsport/TeamOverview.vue';

import { debounce } from 'lodash';

export default {
    name: 'zoezi-teamsport-match',

    zoezi: {
        title: 'Match',
        icon: 'mdi-trophy',
        addon: 'TeamSports'
    },

    props: {
        matchId: {
            title: 'Match ID',
            type: Number
        }
    },

    components: {
        TeamsportMatchResult,
        TeamsportEventDialog,
        TeamsportSetResultDialog,
        TeamsportOneMatch,
        TeamOverview
    },

    data: () => ({
        match: null,
        loading: false,
        unsubscribe: [],
        eventTypes: [],
        rawEvents: [],
        members: [],
        settings: null,
        addEventDialog: {
            show: false,
            eventTypeId: null,
            teamId: null
        },
        changeEventDialog: {
            show: false,
            eventId: null
        },
        playerDetails: {
            show: false,
            user: null,
            teamId: null,
            x: null,
            y: null
        },
        manageFormation: {
            show: false,
            teamId: null,
            group: {},
            activity: {}
        },
        attendance: {},
        showSetResultDialog: false
    }),

    watch: {
        matchId: {
            immediate: true,
            handler() {
                this.getMatchAndMembers();
            }
        },
        match() {
            this.getMembersInMatch();
            this.getAttendance();
        }
    },

    computed: {
        showUserDetails() {
            return this.$store.state.user && (this.$api.hasPermission('Competition handling') || this.isTeamLeader || this.isReferee);
        },
        events() {
            if (!this.rawEvents || !this.eventTypes) {
                return [];
            }
            return this.rawEvents
                .filter(event => event.eventtype_id)
                .map(event => {
                    const eventType = this.eventTypes.find(x => x.id == event.eventtype_id);
                    return {
                        id: event.id,
                        title: eventType.name,
                        subtitle: event.comment,
                        time: event.matchtime,
                        color: eventType.text_color,
                        background_color: eventType.background_color,
                        home: event.team_id == this.match.teams[0].id,
                        comment: event.comment,
                        timeperiod: event.timeperiod,
                        user: this.getUsers(event.team_id == this.match.teams[0].id ? 0 : 1).find(x => x.id == event.user_id)
                    }
                }).sort((a, b) => b.time - a.time);
        },
        groupedEvents() {
            let res = {}
            this.timePeriods.forEach(tp => {
                res[tp.id] = this.events.filter(x => x.timeperiod == tp.id);
            })
            return res;
        },
        eventTypesForSport() {
            return this.match.division.competitionstep.gameclass.competition.teamsport.eventtypes.map(eventtype_id => {
                return this.eventTypes.find(x => x.id == eventtype_id);
            }).filter(x => x?.team).sort((a, b) => a.name.localeCompare(b.name));
        },
        canLiveReport() {
            return this.settings?.live_report && this.canReportEvent();
        },
        canLiveReportForHomeTeam() {
            return this.canLiveReport && this.match && this.canReportEvent(this.match.teams[0]);
        },
        canLiveReportForAwayTeam() {
            return this.canLiveReport && this.match && this.canReportEvent(this.match.teams[1]);
        },
        teamsSet() {
            return !this.match.teams.some(team => !team.id);
        },
        canReportResult() {
            if (!this.$store.state.user) {
                return false;
            }

            if (!this.teamsSet) {
                return false;
            }

            if (this.$api.hasPermission('Competition handling')) {
                return true;
            }

            if (!this.settings) {
                return false;
            }

            const timeOk = this.match.activity && this.$store.state.now30s < Date.newFull(this.match.activity.endTime).addDays(this.settings.result_report_days);
            const refereeCanReport = ['both', 'result'].includes(this.settings.referee_report);
            const teamLeaderCanReport = ['both', 'both_team', 'result'].includes(this.settings.teamleader_report);
            return (this.isReferee && refereeCanReport) || (this.isTeamLeader && teamLeaderCanReport) && (!this.match.finished || timeOk);
        },
        timePeriods() {
            let timeperiods = this.match.division.competitionstep.gameclass.competition.teamsport.timeperiods || '';
            let res = timeperiods.split(',').reverse();
            res.push(null)
            return res.map(id => {
                return {
                    id,
                    title: id || this.$translate('No period')
                }
            })
        },
        isTeamLeader() {
            return this.$store.state.user && this.match.teams.some(team => team.group && team.group.responsible.includes(this.$store.state.user.id));
        },
        referees() {
            if (!this.match || !this.members.length) {
                return [];
            }
            return this.match.referees_id.map(id => {
                const user = this.members.find(x => x.id == id);
                return {
                    name: user.name,
                    id: user.id,
                    phone: user.phone,
                    mail: user.mail
                }
            }).sort((a, b) => a.name.localeCompare(b.name));
        },
        twoTeams() {
            return this.match?.teams.length == 2;
        },
        isReferee() {
            return this.$store.state.user && this.match.referees_id && this.match.referees_id.includes(this.$store.state.user.id);
        },
        isIndividualCompetition() {
            return this.match.division.competitionstep.gameclass.individual;
        },
        opposingTeams() {
            return this.match.teams.slice(0, 2);
        }
    },

    methods: {
        showManageFormationDialog(team) {
            this.manageFormation.show = true;
            this.manageFormation.teamId = team.id;
            this.manageFormation.group = team.group;

            const teamIndex = this.match.teams.findIndex(x => x.id == team.id);
            this.manageFormation.activity = this.match.team_activities[teamIndex];
        },
        getMatchAndMembers() {
            this.loading = true;

            Promise.all([this.getMatch(), this.getMembersInMatch()]).then(() => {
                this.loading = false;
            });
        },
        getMatch() {
            return this.$api.get('/api/teamsport/match/get', {id: this.matchId, full: true}).then(match => {
                this.match = match;
                if (match.activity) {
                    this.$booking.transformWorkout(match.activity);
                }
            }, () => {
                this.match = null;
            });
        },
        getMembersInMatch() {
            return this.$api.get('/api/memberapi/teamsport/get_member_in_match', {match_id: this.matchId}).then(members => {
                this.members = members;
            })
        },
        getEvents() {
            this.$api.get('/api/teamsport/event/get', {match_id: this.matchId}).then(events => {
                this.rawEvents = events;
            })
        },
        getEventTypes() {
            this.$api.get('/api/teamsport/eventtype/get').then(eventTypes => {
                this.eventTypes = eventTypes;
            })
        },
        addEvent(teamId, eventTypeId) {
            this.addEventDialog.teamId = teamId;
            this.addEventDialog.eventTypeId = eventTypeId;
            this.addEventDialog.show = true;
        },
        getUsers(index) {
            if (!this.match || !this.members.length) {
                return [];
            }

            return this.match.team_activities[index].bookings.map(booking => {
                const user = this.members.find(x => x.id == booking.user_id);
                return {
                    name: user.name,
                    id: user.id,
                    phone: user.phone,
                    mail: user.mail
                }
            }).sort((a, b) => a.name.localeCompare(b.name));
        },
        fetchTeamsportSettings() {
            this.$api.get('/api/teamsport/settings/get').then(settings => {
                this.settings = settings;
            })
        },
        canReportEvent(forTeam) {
            if (!this.$store.state.user) {
                return false;
            }

            if (!this.match) {
                return false;
            }

            if (this.match.teams.some(team => !team.id)) {
                return false;
            }

            if (this.$api.hasPermission('Competition handling')) {
                return true;
            }

            if (!this.settings) {
                return false;
            }

            const isTeamLeader = this.isTeamLeader;
            const isTeamLeaderForTeam = !forTeam || (forTeam.group && forTeam.group.responsible.includes(this.$store.state.user.id));
            const timeOk = this.match.activity && this.$store.state.now30s < Date.newFull(this.match.activity.endTime).addDays(this.settings.event_report_days);
            const refereeCanReport = ['both', 'event'].includes(this.settings.referee_report);
            const teamLeaderCanReportForAll = ['both', 'event'].includes(this.settings.teamleader_report);
            const teamLeaderCanReportForOwn = ['both_team', 'event_team'].includes(this.settings.teamleader_report);
            const teamLeaderCanReport = teamLeaderCanReportForAll || (teamLeaderCanReportForOwn && isTeamLeaderForTeam);

            return ((this.isReferee && refereeCanReport) || (isTeamLeader && teamLeaderCanReport)) && (!this.match.finished || timeOk);
        },
        changeEvent(event) {
            if(!this.canReportEvent(this.match.teams.find(x => x.id == event.team_id))) {
                return;
            }
            this.changeEventDialog.eventId = event.id;
            this.changeEventDialog.show = true;
        },
        getAttendance() {
            if (!this.match || !this.$store.state.user) {
                return;
            }
            const teamLeaderTeams = this.match.teams.filter(team => this.isReferee || team?.group?.responsible?.includes(this.$store.state.user.id));
            teamLeaderTeams.forEach(team => {
                let index = this.match.teams.findIndex(x => x.id == team.id)
                const teamActivity = this.match.team_activities[index];
                this.$api.get('/api/memberapi/teamsport/attendance/get', {activityId: teamActivity.id}).then(attendance => {
                    this.$set(this.attendance, team.id, attendance);
                })
            })
        },
        updateAttendance(teamId, data) {
            let o = this.attendance[teamId].find(x => x.id == data.id)
            if (o) {
                o.present = data.present;
            }

            // FIXME: Perhaps fetch the attendance again?
        },
    },
    mounted() {
        this.unsubscribe.push(this.$api.subscribe({type: 'teamsportmatch', callback: (_, event) => {
            if (this.match && event.data.find(x => x.id == this.match.id)) {
                this.getMatchAndMembers();
            }
        }}))
        this.unsubscribe.push(this.$api.subscribe({type: 'teamsportevent', callback: (_, event) => {
            if (this.match && event.data.find(x => x.match_id == this.match.id)) {
                this.getEvents();
            }
        }}))
        this.unsubscribe.push(this.$api.subscribe({type: 'settings:teamsportsettings', callback: () => {
            this.fetchTeamsportSettings();
        }}));
        this.unsubscribe.push(this.$api.subscribe({type: 'workoutbooking', callback: (_, event) => {
            if (!this.match || !this.$store.state.user) {
                return;
            }

            const teamActivityIds = this.match.team_activities.map(activity => activity.id);

            // Check if the event data includes any of the team activity IDs
            const relevantBooking = event.data.find(x => teamActivityIds.includes(x.workout));
            if (relevantBooking) {
                this.getAttendanceAndMatchDebounce();
            }
        }}));
        this.getEventTypes()
        this.getEvents();
        this.fetchTeamsportSettings();
    },
    destroyed() {
        this.unsubscribe.forEach(x => x());
    },
    beforeCreate() {
        this.getAttendanceAndMatchDebounce = debounce(() => {
            this.getAttendance();
            this.getMatchAndMembers();
        }, 500);
    }
}
</script>
